import { useState, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { format, isToday, isWeekend } from 'date-fns';
import { useTimesheetData } from '@/hooks/useTimesheetData';
// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
  Smartphone,
  Monitor,
  Wifi,
  UserPlus,
  Camera,
  Plus,
  X,
  Check,
  Coffee,
  CalendarDays,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE = [0.25, 0.46, 0.45, 0.94];

const METHOD_ICONS = {
  qr: Smartphone,
  kiosk: Monitor,
  nfc: Wifi,
  manual: UserPlus,
};

const METHOD_LABELS = {
  qr: 'QR Code',
  kiosk: 'Kiosk',
  nfc: 'NFC',
  manual: 'Manual',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getPrimaryMethod(methods) {
  if (!methods || methods.length === 0) return null;
  return methods[0];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Period navigation & controls bar */
function PeriodHeader({
  periodLabel,
  periodType,
  goToPrevPeriod,
  goToNextPeriod,
  goToThisWeek,
  goToLastWeek,
  switchPeriodType,
  exportExcel,
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-white border-b">
      {/* Period navigation */}
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToPrevPeriod}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold text-gray-900 min-w-[200px] text-center select-none">
          {periodLabel}
        </span>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToNextPeriod}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Period type toggle */}
      <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
        <button
          onClick={() => switchPeriodType('week')}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
            periodType === 'week'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Week
        </button>
        <button
          onClick={() => switchPeriodType('month')}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
            periodType === 'month'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Month
        </button>
      </div>

      {/* Quick period buttons */}
      <div className="flex items-center gap-1.5 ml-auto">
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={goToThisWeek}>
          This Week
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={goToLastWeek}>
          Last Week
        </Button>
        <Separator orientation="vertical" className="h-6 mx-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={exportExcel}>
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
      </div>
    </div>
  );
}

/** Search and filter bar */
function FilterBar({
  searchQuery,
  setSearchQuery,
  departments,
  departmentFilter,
  setDepartmentFilter,
  methodFilter,
  setMethodFilter,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-gray-50/80 border-b">
      {/* Search */}
      <div className="relative flex-1 min-w-[180px] max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search employees..."
          className="h-8 pl-8 text-sm bg-white"
        />
      </div>

      {/* Department filter */}
      <Select value={departmentFilter || 'all'} onValueChange={(v) => setDepartmentFilter(v === 'all' ? null : v)}>
        <SelectTrigger className="h-8 text-xs w-[160px]">
          <SelectValue placeholder="All Departments" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Departments</SelectItem>
          {departments.map((dept) => (
            <SelectItem key={dept} value={dept}>
              {dept}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Method filter */}
      <Select value={methodFilter || 'all'} onValueChange={(v) => setMethodFilter(v === 'all' ? null : v)}>
        <SelectTrigger className="h-8 text-xs w-[140px]">
          <SelectValue placeholder="All Methods" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Methods</SelectItem>
          <SelectItem value="qr">QR Code</SelectItem>
          <SelectItem value="kiosk">Kiosk</SelectItem>
          <SelectItem value="nfc">NFC</SelectItem>
          <SelectItem value="manual">Manual</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/** Tiny method icon for day cells */
function MethodIcon({ method, className }) {
  const Icon = METHOD_ICONS[method];
  if (!Icon) return null;
  return <Icon className={cn('h-2.5 w-2.5', className)} />;
}

/** Day column header cell */
function DayColumnHeader({ date }) {
  const dayName = format(date, 'EEE');
  const dayNum = format(date, 'd');
  const today = isToday(date);
  const weekend = isWeekend(date);

  return (
    <th
      className={cn(
        'px-1.5 py-2 text-center text-xs font-medium min-w-[64px] border-r border-gray-100 last:border-r-0',
        today && 'bg-blue-50',
        weekend && !today && 'bg-gray-50/60',
        !today && !weekend && 'text-gray-500'
      )}
    >
      <div className={cn('leading-none', today && 'text-blue-700 font-semibold')}>
        {dayName}
      </div>
      <div
        className={cn(
          'mt-0.5 text-[11px] leading-none',
          today
            ? 'bg-blue-600 text-white w-5 h-5 rounded-full flex items-center justify-center mx-auto'
            : 'text-gray-400'
        )}
      >
        {dayNum}
      </div>
    </th>
  );
}

/** Individual day cell in employee row */
function DayCell({ daySummary, formatDuration }) {
  if (!daySummary || !daySummary.hasShifts) {
    return (
      <td className="px-1.5 py-2.5 text-center text-xs text-gray-300 border-r border-gray-100 last:border-r-0">
        -
      </td>
    );
  }

  const method = getPrimaryMethod(daySummary.methods);
  const bgClass = daySummary.isActive
    ? 'bg-amber-50'
    : daySummary.isComplete
    ? 'bg-emerald-50'
    : '';

  return (
    <td className={cn('px-1 py-1 text-center border-r border-gray-100 last:border-r-0', bgClass)}>
      <div className="relative flex flex-col items-center justify-center min-h-[36px]">
        {/* Hours */}
        <span
          className={cn(
            'text-xs font-semibold',
            daySummary.isActive ? 'text-amber-700' : 'text-gray-900'
          )}
        >
          {formatDuration(daySummary.netMinutes)}
        </span>

        {/* Bottom indicators row */}
        <div className="flex items-center gap-0.5 mt-0.5">
          {method && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <MethodIcon
                    method={method}
                    className={cn(
                      daySummary.isActive ? 'text-amber-500' : 'text-gray-400'
                    )}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}>
                {METHOD_LABELS[method] || method}
              </TooltipContent>
            </Tooltip>
          )}
          {daySummary.hasSelfie && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-2 h-2 rounded-full bg-blue-400 block" />
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}>
                Selfie captured
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </td>
  );
}

/** Inline add-break form */
function AddBreakForm({ breakTypes, onSave, onCancel }) {
  const [breakTypeId, setBreakTypeId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedBreakType = breakTypes.find((bt) => bt.id === breakTypeId);

  const handleSave = async () => {
    if (!breakTypeId || !startTime || !endTime) return;
    setSaving(true);
    try {
      await onSave(breakTypeId, startTime, endTime, selectedBreakType?.is_paid ?? false, notes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: EASE }}
      className="overflow-hidden"
    >
      <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-200 mt-2 space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium text-blue-800">
          <Coffee className="h-3.5 w-3.5" />
          Add Break
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Select value={breakTypeId} onValueChange={setBreakTypeId}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Break type" />
            </SelectTrigger>
            <SelectContent>
              {breakTypes.map((bt) => (
                <SelectItem key={bt.id} value={bt.id}>
                  {bt.name} {bt.is_paid ? '(Paid)' : '(Unpaid)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="h-8 text-xs"
            placeholder="Start"
          />
          <Input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="h-8 text-xs"
            placeholder="End"
          />
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          rows={1}
          className="text-xs resize-none"
        />
        <div className="flex items-center gap-2 justify-end">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onCancel}>
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={handleSave}
            disabled={!breakTypeId || !startTime || !endTime || saving}
          >
            <Check className="h-3 w-3 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

/** Expanded detail view for a single employee's day */
function DayDetail({ day, breakTypes, addBreak, onViewSelfie, formatDuration, userId }) {
  const [addBreakShiftId, setAddBreakShiftId] = useState(null);

  if (!day.hasShifts) return null;

  return (
    <div className="py-2">
      <div className="text-xs font-semibold text-gray-700 mb-2">
        {format(day.date, 'EEEE d MMM')}
      </div>
      <div className="space-y-2">
        {day.shifts.map((shift, shiftIdx) => {
          const clockIn = shift.check_in_time ? format(new Date(shift.check_in_time), 'HH:mm') : '--:--';
          const clockOut = shift.check_out_time
            ? format(new Date(shift.check_out_time), 'HH:mm')
            : null;
          const method = shift.check_in_method || shift.method || null;
          const MethodIconComponent = method ? METHOD_ICONS[method] : null;

          return (
            <div key={shift.checkin_id || shiftIdx} className="bg-white rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Times */}
                  <div className="flex items-center gap-1 text-sm font-mono">
                    <span className="text-gray-900">{clockIn}</span>
                    <span className="text-gray-400 mx-0.5">-</span>
                    {clockOut ? (
                      <span className="text-gray-900">{clockOut}</span>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5 py-0">
                        Active
                      </Badge>
                    )}
                  </div>

                  {/* Duration */}
                  {shift.duration_minutes != null && (
                    <span className="text-xs text-gray-500">
                      ({formatDuration(shift.duration_minutes)})
                    </span>
                  )}

                  {/* Method */}
                  {MethodIconComponent && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <MethodIconComponent className="h-3 w-3" />
                      <span>{METHOD_LABELS[method] || method}</span>
                    </div>
                  )}

                  {/* Device */}
                  {shift.device_name && (
                    <span className="text-xs text-gray-400">{shift.device_name}</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Selfie */}
                  {shift.selfie_url && (
                    <button
                      onClick={() => onViewSelfie(shift.selfie_url)}
                      className="w-9 h-9 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors flex-shrink-0 relative group"
                    >
                      <img src={shift.selfie_url} alt="Selfie" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <Camera className="h-3 w-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  )}

                  {/* Add break button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-gray-500 hover:text-blue-600"
                    onClick={() =>
                      setAddBreakShiftId(addBreakShiftId === (shift.checkin_id || shiftIdx) ? null : (shift.checkin_id || shiftIdx))
                    }
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Break
                  </Button>
                </div>
              </div>

              {/* Notes */}
              {shift.notes && (
                <p className="text-xs text-gray-500 mt-1.5 italic">{shift.notes}</p>
              )}

              {/* Breaks */}
              {shift.breaks && shift.breaks.length > 0 && (
                <div className="mt-2 space-y-1">
                  {shift.breaks.map((brk, brkIdx) => {
                    const brkStart = brk.start_time ? format(new Date(brk.start_time), 'HH:mm') : '--:--';
                    const brkEnd = brk.end_time ? format(new Date(brk.end_time), 'HH:mm') : '--:--';
                    return (
                      <div
                        key={brk.id || brkIdx}
                        className="flex items-center gap-2 text-xs text-gray-500 pl-2 border-l-2 border-gray-200"
                      >
                        <Coffee className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        <span className="font-medium">{brk.type_name || 'Break'}</span>
                        <span className="font-mono">
                          {brkStart} - {brkEnd}
                        </span>
                        {brk.duration_minutes != null && (
                          <span>({formatDuration(brk.duration_minutes)})</span>
                        )}
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] px-1 py-0 h-4',
                            brk.is_paid
                              ? 'border-green-300 text-green-700 bg-green-50'
                              : 'border-gray-300 text-gray-600 bg-gray-50'
                          )}
                        >
                          {brk.is_paid ? 'Paid' : 'Unpaid'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add break form */}
              <AnimatePresence>
                {addBreakShiftId === (shift.checkin_id || shiftIdx) && (
                  <AddBreakForm
                    breakTypes={breakTypes}
                    onSave={(breakTypeId, start, end, isPaid, notes) => {
                      // Combine date from shift's check_in_time with time input values
                      const shiftDate = new Date(shift.check_in_time);
                      const dateStr = shiftDate.toISOString().split('T')[0];
                      const startTimestamp = `${dateStr}T${start}:00`;
                      const endTimestamp = end ? `${dateStr}T${end}:00` : null;
                      return addBreak(shift.checkin_id, userId, breakTypeId, startTimestamp, endTimestamp, isPaid, notes);
                    }}
                    onCancel={() => setAddBreakShiftId(null)}
                  />
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Day subtotal */}
      <div className="flex items-center justify-end gap-4 mt-2 text-xs text-gray-500">
        <span>
          Gross: <span className="font-semibold text-gray-700">{formatDuration(day.grossMinutes)}</span>
        </span>
        {day.unpaidBreakMinutes > 0 && (
          <span>
            - Unpaid breaks:{' '}
            <span className="font-semibold text-gray-700">{formatDuration(day.unpaidBreakMinutes)}</span>
          </span>
        )}
        <span>
          = Net: <span className="font-semibold text-gray-900">{formatDuration(day.netMinutes)}</span>
        </span>
      </div>
    </div>
  );
}

/** Expanded detail for an employee showing all days with shifts */
function EmployeeExpandedDetail({ employee, breakTypes, addBreak, onViewSelfie, formatDuration }) {
  const daysWithShifts = employee.dailySummary.filter((d) => d.hasShifts);

  if (daysWithShifts.length === 0) {
    return (
      <div className="px-6 py-4 text-center text-sm text-gray-400">
        No shifts recorded in this period.
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25, ease: EASE }}
      className="overflow-hidden"
    >
      <div className="px-6 py-3 bg-gray-50/70 border-t border-gray-100">
        <div className="divide-y divide-gray-200">
          {daysWithShifts.map((day) => (
            <DayDetail
              key={day.dateStr}
              day={day}
              breakTypes={breakTypes}
              addBreak={addBreak}
              onViewSelfie={onViewSelfie}
              formatDuration={formatDuration}
              userId={employee.user_id}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/** Single employee row */
function EmployeeRow({
  employee,
  periodDays,
  formatDuration,
  breakTypes,
  addBreak,
  onViewSelfie,
  isExpanded,
  onToggle,
  rowIndex,
}) {
  const hasShifts = employee.totalNetMinutes > 0 || employee.totalGrossMinutes > 0;

  // Build a lookup map for day summaries by date string
  const dayMap = useMemo(() => {
    const m = {};
    if (employee.dailySummary) {
      employee.dailySummary.forEach((ds) => {
        m[ds.dateStr] = ds;
      });
    }
    return m;
  }, [employee.dailySummary]);

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: EASE, delay: Math.min(rowIndex * 0.03, 0.3) }}
        onClick={onToggle}
        className={cn(
          'group cursor-pointer transition-colors relative',
          isExpanded ? 'bg-blue-50/40' : 'hover:bg-gray-50/80'
        )}
      >
        {/* Left accent bar */}
        <td className="relative w-0 p-0">
          <div
            className={cn(
              'absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full',
              hasShifts ? 'bg-emerald-500' : 'bg-slate-200'
            )}
          />
        </td>

        {/* Employee info */}
        <td className="pl-4 pr-3 py-2.5 whitespace-nowrap">
          <div className="flex items-center gap-2.5">
            <Avatar className="h-8 w-8 flex-shrink-0">
              {employee.avatar_url && <AvatarImage src={employee.avatar_url} alt={employee.display_name} />}
              <AvatarFallback className="text-[10px] font-semibold bg-gray-200 text-gray-700">
                {getInitials(employee.display_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate max-w-[160px]">
                {employee.display_name}
              </div>
              {employee.department && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 mt-0.5 border-blue-200 text-blue-700 bg-blue-50">
                  {employee.department}
                </Badge>
              )}
            </div>
          </div>
        </td>

        {/* Day cells */}
        {periodDays.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const ds = dayMap[dateStr] || null;
          return (
            <DayCell key={dateStr} daySummary={ds} formatDuration={formatDuration} />
          );
        })}

        {/* Total column */}
        <td className="px-3 py-2.5 text-right whitespace-nowrap border-l border-gray-200">
          <div className="text-sm font-bold text-gray-900">
            {formatDuration(employee.totalNetMinutes)}
          </div>
          {employee.totalUnpaidBreakMinutes > 0 && (
            <div className="text-[10px] text-gray-400 mt-0.5">
              -{formatDuration(employee.totalUnpaidBreakMinutes)} brk
            </div>
          )}
        </td>
      </motion.tr>

      {/* Expanded detail */}
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={periodDays.length + 3} className="p-0">
              <EmployeeExpandedDetail
                employee={employee}
                breakTypes={breakTypes}
                addBreak={addBreak}
                onViewSelfie={onViewSelfie}
                formatDuration={formatDuration}
              />
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

/** Totals footer row */
function TotalsRow({ periodDays, employees, periodTotals, formatDuration }) {
  // Build per-day totals
  const dayTotals = useMemo(() => {
    const totals = {};
    periodDays.forEach((date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      let totalNet = 0;
      employees.forEach((emp) => {
        const ds = emp.dailySummary?.find((d) => d.dateStr === dateStr);
        if (ds) totalNet += ds.netMinutes || 0;
      });
      totals[dateStr] = totalNet;
    });
    return totals;
  }, [periodDays, employees]);

  return (
    <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
      <td className="w-0 p-0" />
      <td className="pl-4 pr-3 py-3 text-sm text-gray-700 uppercase tracking-wide">
        Totals
        <span className="text-xs font-normal text-gray-400 ml-1.5 lowercase tracking-normal">
          ({periodTotals?.employeeCount || employees.length} employees)
        </span>
      </td>
      {periodDays.map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const total = dayTotals[dateStr] || 0;
        return (
          <td key={dateStr} className="px-1.5 py-3 text-center text-xs border-r border-gray-100 last:border-r-0">
            {total > 0 ? (
              <span className="font-semibold text-gray-800">{formatDuration(total)}</span>
            ) : (
              <span className="text-gray-300">-</span>
            )}
          </td>
        );
      })}
      <td className="px-3 py-3 text-right border-l border-gray-200">
        <span className="text-sm font-bold text-gray-900">
          {formatDuration(periodTotals?.netMinutes || 0)}
        </span>
      </td>
    </tr>
  );
}

/** Loading skeleton rows */
function LoadingSkeleton({ periodDays }) {
  const dayCount = periodDays?.length || 7;

  return (
    <tbody>
      {[...Array(5)].map((_, rowIdx) => (
        <tr key={rowIdx} className="border-b border-gray-100">
          <td className="w-0 p-0" />
          <td className="pl-4 pr-3 py-3">
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div>
                <Skeleton className="h-4 w-28 mb-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          </td>
          {[...Array(dayCount)].map((_, colIdx) => (
            <td key={colIdx} className="px-1.5 py-3 text-center border-r border-gray-100 last:border-r-0">
              <Skeleton className="h-4 w-8 mx-auto" />
            </td>
          ))}
          <td className="px-3 py-3 text-right border-l border-gray-200">
            <Skeleton className="h-4 w-10 ml-auto" />
          </td>
        </tr>
      ))}
    </tbody>
  );
}

/** Empty state */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <CalendarDays className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">No timesheet data</h3>
      <p className="text-sm text-gray-500 text-center max-w-xs">
        No clock-in records found for this period. Try adjusting the date range.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TimesheetsView({ projectId, companyId, onViewSelfie }) {
  const {
    // Data
    employees,
    departments,
    breakTypes,
    periodTotals,

    // Period
    periodType,
    periodDays,
    periodLabel,

    // Period navigation
    goToNextPeriod,
    goToPrevPeriod,
    goToThisWeek,
    goToLastWeek,
    switchPeriodType,

    // Filters
    departmentFilter,
    setDepartmentFilter,
    methodFilter,
    setMethodFilter,
    searchQuery,
    setSearchQuery,

    // State
    loading,

    // Actions
    exportExcel,
    addBreak,

    // Formatters
    formatDuration,
  } = useTimesheetData(projectId, companyId);

  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);

  const toggleExpanded = useCallback(
    (userId) => {
      setExpandedEmployeeId((prev) => (prev === userId ? null : userId));
    },
    []
  );

  // Safeguard: default arrays
  const safeEmployees = employees || [];
  const safePeriodDays = periodDays || [];
  const safeDepartments = departments || [];
  const safeBreakTypes = breakTypes || [];

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border shadow-sm overflow-hidden">
      {/* Header bar */}
      <PeriodHeader
        periodLabel={periodLabel}
        periodType={periodType}
        goToPrevPeriod={goToPrevPeriod}
        goToNextPeriod={goToNextPeriod}
        goToThisWeek={goToThisWeek}
        goToLastWeek={goToLastWeek}
        switchPeriodType={switchPeriodType}
        exportExcel={exportExcel}
      />

      {/* Filter bar */}
      <FilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        departments={safeDepartments}
        departmentFilter={departmentFilter}
        setDepartmentFilter={setDepartmentFilter}
        methodFilter={methodFilter}
        setMethodFilter={setMethodFilter}
      />

      {/* Table container */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <tr>
                <th className="w-0 p-0" />
                <th className="pl-4 pr-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                  Employee
                </th>
                {safePeriodDays.map((date) => (
                  <DayColumnHeader key={format(date, 'yyyy-MM-dd')} date={date} />
                ))}
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] border-l border-gray-200">
                  Total
                </th>
              </tr>
            </thead>
            <LoadingSkeleton periodDays={safePeriodDays} />
          </table>
        ) : safeEmployees.length === 0 ? (
          <EmptyState />
        ) : (
          <table className="w-full border-collapse">
            {/* Column headers */}
            <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <tr>
                <th className="w-0 p-0" />
                <th className="pl-4 pr-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                  Employee
                </th>
                {safePeriodDays.map((date) => (
                  <DayColumnHeader key={format(date, 'yyyy-MM-dd')} date={date} />
                ))}
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] border-l border-gray-200">
                  Total
                </th>
              </tr>
            </thead>

            {/* Employee rows */}
            <tbody className="divide-y divide-gray-100">
              {safeEmployees.map((employee, idx) => (
                <EmployeeRow
                  key={employee.user_id}
                  employee={employee}
                  periodDays={safePeriodDays}
                  formatDuration={formatDuration}
                  breakTypes={safeBreakTypes}
                  addBreak={addBreak}
                  onViewSelfie={onViewSelfie}
                  isExpanded={expandedEmployeeId === employee.user_id}
                  onToggle={() => toggleExpanded(employee.user_id)}
                  rowIndex={idx}
                />
              ))}
            </tbody>

            {/* Totals footer */}
            <tfoot>
              <TotalsRow
                periodDays={safePeriodDays}
                employees={safeEmployees}
                periodTotals={periodTotals}
                formatDuration={formatDuration}
              />
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
